import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Loader, AlertTriangle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ProfilePreview from '../components/ProfilePreview';
import ShareModal from '../components/ShareModal';
import { getLocaleFromPathname } from '../utils/locale';

// 이미지 경로 헬퍼
const getImageUrl = (filename) => {
  if (!filename) return null;
  if (filename.startsWith('blob:')) return filename;
  // [중요] 배포 환경 URL
  return `https://disparty.com/uploads/${filename}`;
};

const buildProfileDescription = (profile, fallback) => {
  const parts = [profile.description];

  profile.blocks?.forEach((block) => {
    if (block.type === 'text') parts.push(block.content);
    if (block.type === 'link') parts.push(block.title);
    if (block.type === 'music') parts.push(block.title);
  });

  const description = parts
    .filter(Boolean)
    .map((part) => String(part).replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .filter((part, index, values) => values.indexOf(part) === index)
    .join(' ');

  if (!description) return fallback;
  return description.length > 220 ? `${description.slice(0, 220)}...` : description;
};

function PublicProfilePage() {
  const { uniqueId, locale: routeLocale } = useParams();
  const location = useLocation();
  const locale = routeLocale || getLocaleFromPathname(location.pathname);
  const isVi = locale === "vi";
  const tx = useCallback((ko, vi) => (isVi ? vi : ko), [isVi]);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareUrl = window.location.href;

  useEffect(() => {
    if (!uniqueId) return;

    const fetchPublicProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/profile/${uniqueId}?locale=${locale}`);
        if (response.status === 404) throw new Error(tx('프로필을 찾을 수 없습니다.', 'Không tìm thấy hồ sơ.'));
        if (!response.ok) throw new Error(tx('오류 발생', 'Đã xảy ra lỗi'));

        const data = await response.json();

        // 서버의 links 데이터를 blocks로 복구하는 로직
        let parsedBlocks = [];
        let rawLinks = [];

        try {
          if (data.links && data.links.Valid && data.links.String) {
            rawLinks = JSON.parse(data.links.String);
          } else if (typeof data.links === 'string') {
            rawLinks = JSON.parse(data.links);
          } else if (Array.isArray(data.links)) {
            rawLinks = data.links;
          }
        } catch (e) {
            rawLinks = [];
        }

        parsedBlocks = rawLinks.map((link, index) => {
            const url = link.url || '';

            if (url.startsWith('internal://type=text')) {
                const sizeMatch = url.match(/size=([^&]+)/);
                const size = sizeMatch ? sizeMatch[1] : 'medium';
                return {
                    id: `recovered-${index}`,
                    type: 'text',
                    content: link.title,
                    size: link.style?.size || size,
                    textColor: link.style?.textColor || '#1f2937',
                    align: link.style?.align || 'center'
                };
            }

            if (url.startsWith('internal://type=image')) {
                return {
                    id: `recovered-${index}`,
                    type: 'image',
                    content: link.title
                };
            }

            if (url.startsWith('internal://type=music')) {
                try {
                    const music = JSON.parse(link.title || '{}');
                    return {
                        id: `recovered-${index}`,
                        type: 'music',
                        title: music.title || '',
                        url: music.url || '',
                        textColor: music.textColor || ''
                    };
                } catch (e) {
                    return null;
                }
            }

            if (url.startsWith('internal://type=ai')) {
                const variantMatch = url.match(/[?&]variant=(default|discord|bubble)/);
                return {
                    id: `recovered-${index}`,
                    type: 'ai',
                    title: link.title || tx('AI에게 물어보기', 'Hoi AI'),
                    variant: variantMatch?.[1] || link.style?.variant || 'default',
                    textColor: link.style?.textColor || ''
                };
            }

            if (url.includes('#type=video')) {
                 return {
                    id: `recovered-${index}`,
                    type: 'video',
                    title: link.title,
                    url: url.replace('#type=video', '')
                };
            }

            // [수정됨] SNS 블록 파싱 로직 변경
            if (url.startsWith('internal://type=sns')) {
                let snsItems = [];
                try {
                    const parsedData = JSON.parse(link.title || '{}');
                    
                    // 1. 신규 방식 (배열인 경우 그대로 사용)
                    if (Array.isArray(parsedData)) {
                        snsItems = parsedData;
                    } 
                    // 2. 구형 방식 호환성 유지 (객체인 경우 배열로 변환)
                    else {
                        if (parsedData.instagram_url) snsItems.push({ platform: 'instagram', url: parsedData.instagram_url });
                        if (parsedData.x_url) snsItems.push({ platform: 'x', url: parsedData.x_url });
                        if (parsedData.youtube_url) snsItems.push({ platform: 'youtube', url: parsedData.youtube_url });
                        if (parsedData.discord_url) snsItems.push({ platform: 'discord', url: parsedData.discord_url });
                    }
                } catch (e) {
                    snsItems = [];
                }

                return {
                    id: `recovered-${index}`,
                    type: 'sns',
                    items: snsItems // items 배열로 통일하여 전달
                };
            }

            if (!link.url && !link.title) return null;
            return {
                id: `recovered-${index}`,
                type: 'link',
                title: link.title,
                url: link.url,
                size: link.style?.size || 'medium',
                buttonColor: link.style?.buttonColor || data.button_color || '#6366f1',
                textColor: link.style?.textColor || '#ffffff',
                shape: link.style?.shape || data.button_shape || 'rounded',
                align: link.style?.align || 'center'
            };
        }).filter(block => block !== null);

        setProfile({
            ...data,
            profile_image_url: data.profile_image_url?.Valid ? data.profile_image_url.String : data.profile_image_url,
            background_image_url: data.background_image_url?.Valid ? data.background_image_url.String : data.background_image_url,
            display_name: data.display_name?.Valid ? data.display_name.String : data.display_name,
            description: data.description?.Valid ? data.description.String : data.description,
            blocks: parsedBlocks
        });

      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublicProfile();
  }, [uniqueId, locale, tx]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white">
        <Loader className="animate-spin w-12 h-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold mb-2">{tx("오류", "Lỗi")}</h1>
        <p className="text-gray-400 mb-6">{error}</p>
        <Link to={`/${locale}`} className="bg-indigo-600 text-white px-4 py-2 rounded-lg">
          {tx("홈으로 돌아가기", "Về trang chủ")}
        </Link>
      </div>
    );
  }

  if (!profile) return null;

  const pageTitle = `${profile.display_name} | Disparty 프로필`;
  const pageDesc = buildProfileDescription(
    profile,
    tx('나만의 프로필과 링크를 공유하는 Disparty 페이지입니다.', 'Trang multi-link cua ban tren Disparty.')
  );
  const pageImage = getImageUrl(profile.profile_image_url);

  const DispartyButton = () => (
    <div
      className="flex w-full justify-center px-4 pb-8 pt-10"
      style={{ backgroundColor: profile.background_color || '#ffffff' }}
    >
      <Link
        to={`/${locale}/design`}
        className="flex items-center rounded-full border border-gray-700 bg-gray-800/90 px-6 py-3 text-white shadow-lg transition-colors hover:bg-gray-700"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
        {tx("나만의 페이지 만들기", "Tạo trang của tôi")}
      </Link>
    </div>
  );

  return (
    <div className="relative isolate min-h-[100dvh] bg-[#111216] md:px-8 md:py-12">
      <div aria-hidden="true" className="fixed inset-0 -z-10 bg-[#111216]" />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
      </Helmet>

      <main
        className="relative z-10 mx-auto min-h-[100dvh] w-full overflow-hidden md:max-w-[640px] md:rounded-[2rem] md:shadow-[0_28px_90px_rgba(15,23,42,0.22)]"
        style={{ backgroundColor: profile.background_color || '#ffffff' }}
      >
        <ProfilePreview
          profile={profile}
          onShareClick={() => setIsShareModalOpen(true)}
          locale={locale}
          displayMode="page"
        />
        <DispartyButton />
      </main>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareUrl={shareUrl}
        profile={profile}
      />
    </div>
  );
}

export default PublicProfilePage;
